import React from 'react';
import { Alert, StyleSheet, Text, View } from '../base_components';

import { BusyComponent } from '../busy_component';
import TextButton from '../buttons/text_button';
import QrOverlay from './qr_overlay';
import DialogOverlay from './dialog_overlay';
import ImageButton from '../buttons/image_button';

import WalletStore from '../../stores/wallet_store';

const styles = StyleSheet.create({
  walletOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  innerOverlay: {
    width: '29rem',
    paddingTop: '1.2rem',
    paddingBottom: '2rem',
    borderColor: '#ccc',
    borderWidth: 1,
    borderRadius: '0.8rem',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    marginLeft: '1.5rem',
    alignSelf: 'center',
    color: '#333',
    fontSize: '2rem',
    fontWeight: '500',
  },
  subtitle: {
    marginTop: '1.5rem',
    marginLeft: '1.5rem',
    marginRight: '1.5rem',
    color: '#555',
    fontSize: '1.5rem',
  },
  addressView: {
    marginTop: '1rem',
    height: '3rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    backgroundColor: '#eee',
    borderColor: '#ccc',
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: '0.4rem',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  address: {
    color: '#333',
    fontSize: '1.5rem',
    fontWeight: '500',
  },
  closeButton: {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    width: '1.2rem',
    height: '1.2rem',
    padding: '0.9rem',
    borderRadius: '0.5rem',
  },
  button: {
    marginTop: '1.5rem',
    marginLeft: '1.5rem',
    marginRight: '1.5rem',
    alignSelf: 'stretch',
  },
});

const CLOSE_IMG = require('../../assets/img/close_black.png');

type Props = {
  style?: any;
  onDone: () => void;
};
type State = {
  is_loading: boolean;
  has_wallet: boolean;
  wallet_addr: string;
  wallet_type: string;
  connect_url: string;
};
export default class WalletOverlay extends BusyComponent<Props, State> {
  state = {
    is_loading: true,
    has_wallet: WalletStore.hasWallet(),
    wallet_addr: WalletStore.getAddress(),
    wallet_type: WalletStore.getWalletType(),
    connect_url: '',
  };
  componentDidMount() {
    WalletStore.addChangeListener(this._onWalletChange);
    this._onWalletChange();
  }
  componentWillUnmount() {
    WalletStore.removeChangeListener(this._onWalletChange);
  }
  _onWalletChange = (reason?: string, connect_url?: string) => {
    if (reason === 'connect_url' && connect_url) {
      this.setState({ connect_url });
    } else {
      const has_wallet = WalletStore.hasWallet();
      const wallet_addr = WalletStore.getAddress();
      const wallet_type = WalletStore.getWalletType();
      this.setState({ has_wallet, wallet_addr, wallet_type });
    }
  };
  _onWalletConnect = () => {
    if (this.setBusy()) {
      WalletStore.createWalletConnect((err, extra) => {
        this.clearBusy();
        if (err === 'user_cancel') {
          // noop
        } else if (err) {
          Alert.alert('Wallet Connect', 'Connect failed, please try again.');
        } else {
          this.props.onDone();
        }
        this.setState({ connect_url: '' });
      });
    }
  };
  _onCancelWalletConnect = () => {
    WalletStore.cancelWalletConnect();
  };
  _onWeb3Connect = () => {
    if (this.setBusy()) {
      WalletStore.createEthBrowser((err) => {
        this.clearBusy();
        if (err === 'user_cancel') {
          // noop
        } else if (err) {
          Alert.alert(
            'Wallet Connect',
            'Please enable your wallet plugin for this site.'
          );
        } else {
          this.props.onDone();
        }
      });
    }
  };
  _onDisconnectPress = () => {
    WalletStore.deleteWallet();
  };

  render() {
    const { style, onDone } = this.props;
    const {
      is_busy,
      has_wallet,
      wallet_addr,
      wallet_type,
      connect_url,
    } = this.state;

    let overlay = null;
    if (is_busy && connect_url) {
      overlay = (
        <QrOverlay
          text='Scan QR Code with your Etherium Wallet'
          url={connect_url}
          onDone={this._onCancelWalletConnect}
        />
      );
    }
    let content = null;
    if (has_wallet) {
      const subtitle =
        wallet_type === 'WALLETCONNECT'
          ? 'Connected with Wallet Connect'
          : 'Connected with Web3';
      content = (
        <>
          <Text style={styles.title}>Current Connection</Text>
          <Text style={styles.subtitle}>{subtitle}</Text>
          <View style={styles.addressView}>
            <Text style={styles.address}>{_smartTrim(wallet_addr, 22)}</Text>
          </View>
          <TextButton
            style={styles.button}
            type='default'
            text='Disconnect'
            onPress={this._onDisconnectPress}
          />
        </>
      );
    } else {
      content = (
        <>
          <Text style={styles.title}>Connect Wallet</Text>
          <TextButton
            style={styles.button}
            type='default'
            text='Wallet Connect'
            onPress={this._onWalletConnect}
          />
          <TextButton
            style={styles.button}
            type='default'
            text='Connect Web3 (Metamask)'
            onPress={this._onWeb3Connect}
          />
        </>
      );
    }
    return (
      <View style={styles.walletOverlay}>
        <DialogOverlay
          style={[styles.innerOverlay, style]}
          onOutsidePress={onDone}
        >
          {content}
          <ImageButton
            style={styles.closeButton}
            underlayColor='rgba(0,0,0,0.3)'
            source={CLOSE_IMG}
            onPress={onDone}
          />
        </DialogOverlay>
        {overlay}
      </View>
    );
  }
}

function _smartTrim(string, max_length) {
  let ret = string;
  if (string && string.length > max_length) {
    const midpoint = Math.ceil(string.length / 2);
    const remove_count = string.length - max_length;
    const lstrip = Math.ceil(remove_count / 2);
    const rstrip = remove_count - lstrip;
    ret =
      string.substring(0, midpoint - lstrip) +
      '...' +
      string.substring(midpoint + rstrip);
  }
  return ret;
}
